import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import HeaderScreen from 'src/components/shared/header-screen/HeaderScreen'
import { useDispatch, useSelector } from 'react-redux'
import {
  disConectXero,
  getVenue,
  xeroIntegration,
} from 'src/features/VenueSlice'
import { Button, Input, Modal, Select, Spin, Switch } from 'antd'
import { Link } from 'react-router-dom'
import { Banner } from '../menu-builder/RecipeDashboard'
import demoVideo from '../../assets/vidoes/Xero_Integration.mp4'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import LinkVenuesModal from './xero/LinkVenuesModal'
import SendUnsentXeroModal from './xero/SendUnsentXeroModal'
import {
  fetchLinkedVenues,
  fetchXeroHealth,
  fetchXeroOrganizationData,
  fetchXeroTrackingCategories,
  refreshXeroTrackingCategories,
  saveXeroTrackingMapping,
} from 'src/features/XeroIntegrationSlice'
import {
  SuccessNotification,
  RejectedNotification,
} from 'src/notifications/SuccessNotification'
import { editVenue } from 'src/features/VenueSlice'
import { getVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import {
  XERO_AUTO_SEND_BODY,
  XERO_AUTO_SEND_MAP_SUPPLIERS_CTA,
  XERO_AUTO_SEND_MAP_SUPPLIERS_HINT,
  XERO_AUTO_SEND_SUPPLIER_HINT,
  XERO_AUTO_SEND_TOGGLE_LABEL,
  buildVenueSuppliersPath,
  buildXeroAutoSendVenueUpdate,
  countSuppliersWithXeroDefaults,
  getXeroAutoSendIntegrationsGate,
  readXeroAutoSendEnabled,
  shouldShowXeroMapSuppliersCta,
} from 'src/utils/xeroAutoSendCopy'
import {
  buildXeroDefaultOrganizationUpdate,
  buildXeroFallbackAccountUpdate,
  resolveXeroOrganization,
} from 'src/utils/xeroOrganization'

const { Option } = Select

const Integrations = ({ embedded = false } = {}) => {
  const blocked = useIsBlocked('integrations')

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const venueId = useSelector(state => state.venue?.venue?._id)
  const venue = useSelector(state => state.venue?.venue)
  const dispatch = useDispatch()
  const [isXeroModalVisible, setIsXeroModalVisible] = useState(false)
  const [isLinkVenuesModalVisible, setIsLinkVenuesModalVisible] =
    useState(false)
  const [isDisconnectModalVisible, setIsDisconnectModalVisible] =
    useState(false)
  const [isSendUnsentVisible, setIsSendUnsentVisible] = useState(false)
  const [linkedVenuesInfo, setLinkedVenuesInfo] = useState(null)
  const [newXeroEmail, setNewXeroEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [autoSendLoading, setAutoSendLoading] = useState(false)
  const [xeroHealth, setXeroHealth] = useState(null)
  const [destinationOrgSaving, setDestinationOrgSaving] = useState(false)
  const [fallbackSaving, setFallbackSaving] = useState(false)
  const automaticSendToXero = readXeroAutoSendEnabled(venue)
  const xeroConnected = Boolean(venue?.xeroConnection?.accessToken)
  const xeroExpired = Boolean(venue?.xeroConnectionExpired)
  const destinationOrg = resolveXeroOrganization(venue?.xeroConnection)
  const autoSendGate = getXeroAutoSendIntegrationsGate({
    xeroConnected,
    xeroExpired,
  })
  const venueSuppliers = useSelector(state =>
    (state.venueSuppliers.venueSuppliers || []).filter(
      venueSupplier => venueSupplier.isActive,
    ),
  )
  const showMapSuppliersCta = shouldShowXeroMapSuppliersCta({
    xeroConnected,
    xeroExpired,
    autoSendEnabled: automaticSendToXero,
    mappedSupplierCount: countSuppliersWithXeroDefaults(venueSuppliers),
    activeSupplierCount: venueSuppliers.length,
  })

  const refreshXeroHealth = async () => {
    if (!venueId || !xeroConnected) {
      setXeroHealth(null)
      return
    }
    const data = await dispatch(fetchXeroHealth(venueId))
    setXeroHealth(data)
  }

  useEffect(() => {
    if (venueId && xeroConnected) {
      dispatch(getVenueSuppliers(venueId))
      refreshXeroHealth()
    }
  }, [venueId, xeroConnected, dispatch])

  const xeroIntegrationState = useSelector(state => state.xeroIntegration) || {}
  const trackingCategories = xeroIntegrationState.trackingCategories || []
  const trackingMapping = xeroIntegrationState.trackingMapping || {
    trackingCategoryId: '',
    trackingOptionId: '',
  }
  const trackingLoading = !!xeroIntegrationState.trackingLoading
  const trackingSaving = !!xeroIntegrationState.trackingSaving

  const xeroOrgs = venue?.xeroConnection?.organizations || []
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [selectedTrackingCategoryId, setSelectedTrackingCategoryId] =
    useState('')
  const [selectedTrackingOptionId, setSelectedTrackingOptionId] = useState('')
  const accounts = xeroIntegrationState.accounts || []

  useEffect(() => {
    if (!venue?.xeroConnection?.accessToken) return
    const resolved = resolveXeroOrganization(venue.xeroConnection)
    if (resolved?.id) {
      setSelectedOrgId(resolved.id)
    } else if (xeroOrgs.length > 0 && !selectedOrgId) {
      setSelectedOrgId(xeroOrgs[0].id)
    }
  }, [venue?.xeroConnection, xeroOrgs, selectedOrgId])

  useEffect(() => {
    if (!venue?.xeroConnection?.accessToken || !selectedOrgId) return
    dispatch(fetchXeroOrganizationData(selectedOrgId, venueId))
  }, [selectedOrgId, venue?.xeroConnection?.accessToken, venueId, dispatch])

  useEffect(() => {
    if (!venue?.xeroConnection?.accessToken) return
    if (!venueId || !selectedOrgId) return
    ;(async () => {
      const data = await dispatch(
        fetchXeroTrackingCategories(venueId, selectedOrgId),
      )
      const mapping = data?.trackingMapping
      if (mapping) {
        setSelectedTrackingCategoryId(mapping.trackingCategoryId || '')
        setSelectedTrackingOptionId(mapping.trackingOptionId || '')
      } else {
        setSelectedTrackingCategoryId('')
        setSelectedTrackingOptionId('')
      }
    })()
  }, [dispatch, selectedOrgId, venue?.xeroConnection?.accessToken, venueId])

  // Keep local selection in sync with saved mapping
  useEffect(() => {
    setSelectedTrackingCategoryId(trackingMapping.trackingCategoryId || '')
    setSelectedTrackingOptionId(trackingMapping.trackingOptionId || '')
  }, [trackingMapping.trackingCategoryId, trackingMapping.trackingOptionId])

  const selectedCategory = useMemo(() => {
    return trackingCategories.find(
      c => c?.TrackingCategoryID === selectedTrackingCategoryId,
    )
  }, [trackingCategories, selectedTrackingCategoryId])

  const trackingOptions = useMemo(() => {
    return selectedCategory?.Options || []
  }, [selectedCategory])

  const trackingNotApplied =
    !selectedTrackingCategoryId || !selectedTrackingOptionId

  // useEffect(() => {
  //   // Initialize the email state with the stored value if available
  //   if (venue?.xeroConnection?.contactEmail) {
  //     setXeroEmail(venue.xeroConnection.contactEmail)
  //   }
  // }, [venue])

  const handleXeroConnect = async () => {
    if (venue?.xeroConnection?.accessToken) {
      // If already connected, check for linked venues before disconnecting
      setIsLoading(true)
      const linkedData = await dispatch(fetchLinkedVenues(venueId))
      setLinkedVenuesInfo(linkedData)
      setIsLoading(false)
      setIsDisconnectModalVisible(true)
      return
    }

    // Skip the updates-email modal when the venue already has one saved
    const existingUpdatesEmail = String(
      venue?.account?.financeEmail || '',
    ).trim()
    if (existingUpdatesEmail) {
      await startXeroConnection(existingUpdatesEmail)
      return
    }

    setIsXeroModalVisible(true)
    setNewXeroEmail('')
  }

  const handleConfirmDisconnect = async () => {
    setIsLoading(true)
    const result = await dispatch(disConectXero(venueId))
    if (result?.success) {
      setIsLoading(false)
      setIsDisconnectModalVisible(false)
      setLinkedVenuesInfo(null)
      SuccessNotification({
        message: 'Xero disconnected',
        description:
          result?.message ||
          'Xero account has been disconnected from all linked venues.',
      })
      setTimeout(() => {
        window.location.reload()
      }, 5000)
    } else {
      setIsLoading(false)
      RejectedNotification({
        message: 'Disconnect failed',
        description:
          result?.message || 'Could not disconnect Xero. Please try again.',
      })
    }
  }

  const handleLinkOtherVenues = () => {
    setIsLinkVenuesModalVisible(true)
  }

  const startXeroConnection = async contactEmail => {
    console.log('[Integrations] Starting xero integration for venue:', venueId)
    const result = await dispatch(xeroIntegration(venueId, contactEmail))
    console.log('[Integrations] Result from xeroIntegration:', result)

    // If other venues are already connected, show Link Venues modal instead of OAuth
    if (result?.hasExistingConnection) {
      console.log('[Integrations] Showing Link Venues modal')
      SuccessNotification({
        message: 'Existing Xero Connection Found',
        description: `You have ${
          result.connectedVenues?.length || 0
        } venue(s) already connected to Xero. You can link this venue to an existing connection.`,
      })
      setIsLinkVenuesModalVisible(true)
    } else {
      console.log(
        '[Integrations] No existing connection or redirecting to OAuth',
      )
    }
    // Otherwise, user will be redirected to Xero OAuth (handled in xeroIntegration action)
  }

  const handleXeroModalSubmit = async () => {
    setIsXeroModalVisible(false)
    await startXeroConnection(newXeroEmail)
  }

  // const handleEmailUpdate = () => {
  //   setUpdatingEmail(true)
  //   return dispatch(
  //     updateXeroContactEmail(venueId, xeroEmail),
  //   )
  //     .then(() => {
  //       setIsEmailEditMode(false)
  //       setUpdatingEmail(false)
  //       return true // Return a value from then()
  //     })
  //     .catch(error => {
  //       setUpdatingEmail(false)
  //       console.error('Failed to update email:', error)
  //     })
  // }

  return (
    <Wrapper $embedded={embedded}>
      {embedded ? null : <HeaderScreen title='Your Active Integrations' />}
      <Section>
        <SectionHeader>
          <h2>Xero Account</h2>
        </SectionHeader>
        <Banner style={{ width: '50%', margin: 'auto', marginBottom: '20px' }}>
          <div style={{ textAlign: 'left', maxWidth: '900px' }}>
            <p style={{ fontSize: 20, marginBottom: 10 }}>
              🔗 <strong>Connect to Xero</strong>
            </p>
            <p>
              {`Sync your OMS orders and invoices with Xero to simplify your bookkeeping.`}{' '}
            </p>
            <video
              controls
              style={{
                width: '100%',
                maxWidth: '600px',
                borderRadius: '8px',
                marginBottom: '16px',
                marginTop: '10px',
                boxSizing: 'border-box',
                border: '1px solid #ddd',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              <source src={demoVideo} type='video/mp4' />
              Your browser does not support the video tag.
            </video>

            <p>
              💡 <strong>This tool helps you:</strong>
            </p>
            <ul style={{ marginLeft: 20 }}>
              <li>
                Auto-send reconciled invoices to Xero (when the toggle below is
                on)
              </li>
              <li>Reduce manual data entry</li>
              <li>
                Map each supplier’s Xero account and contact under Your
                Suppliers — OMS will not send without both
              </li>
            </ul>
            <p style={{ marginTop: 12 }}>
              <strong>Checklist:</strong> connect Xero → pick destination org
              (if more than one) → map suppliers → turn auto-send on → set
              fallback account for Force send → send any leftover unsent orders.
            </p>
          </div>
        </Banner>
        <Card>
          <p>
            {venue?.xeroConnection?.accessToken
              ? "You've connected your Xero Account that helps automatically create bills, attach invoices, and simplify your accounting workflow!"
              : 'Connect your Xero account to automatically create bills, attach invoices, and simplify your accounting workflow!'}
          </p>

          {venue?.xeroConnection?.accessToken &&
            venue?.xeroConnection?.connectedUserEmail && (
              <XeroAccountInfo>
                <strong>Connected Xero account:</strong>{' '}
                {venue?.xeroConnection?.connectedUserEmail || '—'}
              </XeroAccountInfo>
            )}

          {/* {venue?.xeroConnection?.accessToken && (
            <EmailContainer>
              <label>Xero Notification Email:</label>
              {isEmailEditMode ? (
                <EmailEditContainer>
                  <Input
                    value={xeroEmail}
                    onChange={e => setXeroEmail(e.target.value)}
                    placeholder='Enter finance contact email'
                  />   
                  <ButtonGroup>
                    <Button
                      size='small'
                      onClick={() => setIsEmailEditMode(false)}
                      disabled={updatingEmail}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='primary'
                      size='small'
                      onClick={handleEmailUpdate}
                      loading={updatingEmail}
                    >
                      Save
                    </Button>
                  </ButtonGroup>
                </EmailEditContainer>
              ) : (
                <EmailDisplay>
                  <span>
                    {venue?.xeroConnection?.contactEmail || 'Not set'}
                  </span>
                  <Tooltip title='Edit email address'>
                    <EditIcon onClick={() => setIsEmailEditMode(true)} />
                  </Tooltip>
                </EmailDisplay>
              )}
              <HelperText>
                {` We'll send expiry alerts and reminders here to make sure your finance team stays in the loop.`}
              </HelperText>
            </EmailContainer>
          )} */}

          <ButtonGroup>
            <Button
              style={{
                background: venue?.xeroConnection?.accessToken
                  ? '#ff4d4d'
                  : '#b5d922',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
              }}
              loading={isLoading}
              onClick={handleXeroConnect}
            >
              <span>
                {venue?.xeroConnection?.accessToken
                  ? 'Disconnect Xero'
                  : 'Connect Xero Account'}
              </span>
            </Button>

            {venue?.xeroConnection?.accessToken && (
              <Button
                style={{
                  background: '#1890ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                }}
                onClick={handleLinkOtherVenues}
              >
                Link Other Venues
              </Button>
            )}
          </ButtonGroup>
        </Card>

        {venue?.xeroConnection?.accessToken ? (
          <Card>
            <SectionHeader>
              <h3>Xero health</h3>
              <Button size='small' onClick={refreshXeroHealth}>
                Refresh
              </Button>
            </SectionHeader>
            <HealthGrid>
              <HealthItem>
                <HealthLabel>Connection</HealthLabel>
                <div>
                  {xeroExpired
                    ? 'Needs reconnect'
                    : xeroConnected
                    ? 'Connected'
                    : 'Not connected'}
                </div>
              </HealthItem>
              <HealthItem>
                <HealthLabel>Destination org</HealthLabel>
                <div>
                  {xeroHealth?.destinationOrg?.name ||
                    destinationOrg?.name ||
                    '—'}
                </div>
              </HealthItem>
              <HealthItem>
                <HealthLabel>Auto-send</HealthLabel>
                <div>{automaticSendToXero ? 'On' : 'Off'}</div>
              </HealthItem>
              <HealthItem>
                <HealthLabel>Unmapped suppliers</HealthLabel>
                <div>
                  {xeroHealth?.unmappedSupplierCount ?? '—'}{' '}
                  <Link to={buildVenueSuppliersPath(venueId)}>
                    Your Suppliers
                  </Link>
                </div>
              </HealthItem>
              <HealthItem>
                <HealthLabel>Orders not in Xero</HealthLabel>
                <div>
                  {xeroHealth?.unsentOrderCount ?? '—'}{' '}
                  <Button
                    type='link'
                    size='small'
                    style={{ padding: 0 }}
                    onClick={() => setIsSendUnsentVisible(true)}
                  >
                    Send unsent
                  </Button>
                </div>
              </HealthItem>
              <HealthItem>
                <HealthLabel>Fallback account</HealthLabel>
                <div>
                  {xeroHealth?.fallbackAccount?.name ||
                    venue?.xeroConnection?.fallbackPurchasesAccountName ||
                    'Not set'}
                </div>
              </HealthItem>
            </HealthGrid>

            {xeroOrgs.length >= 2 ? (
              <FieldRow>
                <FieldLabel>Send bills to</FieldLabel>
                <Spin
                  spinning={destinationOrgSaving}
                  tip='Saving destination organisation…'
                >
                  <Select
                    style={{ width: '100%' }}
                    value={destinationOrg?.id || selectedOrgId || undefined}
                    loading={destinationOrgSaving}
                    disabled={destinationOrgSaving}
                    onChange={async organizationId => {
                      setDestinationOrgSaving(true)
                      try {
                        await dispatch(
                          editVenue(
                            buildXeroDefaultOrganizationUpdate(
                              venue.xeroConnection,
                              organizationId,
                            ),
                            venueId,
                          ),
                        )
                        setSelectedOrgId(organizationId)
                        await refreshXeroHealth()
                      } finally {
                        setDestinationOrgSaving(false)
                      }
                    }}
                  >
                    {xeroOrgs.map(org => (
                      <Option key={org.id} value={org.id}>
                        {org.name}
                      </Option>
                    ))}
                  </Select>
                </Spin>
              </FieldRow>
            ) : null}

            <FieldRow>
              <FieldLabel>Fallback purchases account</FieldLabel>
              <Spin spinning={fallbackSaving} tip='Saving fallback account…'>
                <Select
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  optionFilterProp='children'
                  placeholder='Used for Force send if contact fails'
                  loading={fallbackSaving || xeroIntegrationState.isLoading}
                  disabled={fallbackSaving || xeroIntegrationState.isLoading}
                  value={
                    venue?.xeroConnection?.fallbackPurchasesAccountId ||
                    undefined
                  }
                  onChange={async accountId => {
                    setFallbackSaving(true)
                    try {
                      const account = (accounts || []).find(
                        a => a.AccountID === accountId,
                      )
                      await dispatch(
                        editVenue(
                          buildXeroFallbackAccountUpdate(
                            accountId
                              ? account || { AccountID: accountId }
                              : null,
                          ),
                          venueId,
                        ),
                      )
                      await refreshXeroHealth()
                    } finally {
                      setFallbackSaving(false)
                    }
                  }}
                >
                  {(accounts || []).map(account => (
                    <Option key={account.AccountID} value={account.AccountID}>
                      {account.Name}
                    </Option>
                  ))}
                </Select>
              </Spin>
            </FieldRow>
          </Card>
        ) : null}

        <Card>
          <AutoSendRow>
            <div>
              <AutoSendTitle>{XERO_AUTO_SEND_TOGGLE_LABEL}</AutoSendTitle>
              <AutoSendBody>
                {XERO_AUTO_SEND_BODY} {XERO_AUTO_SEND_SUPPLIER_HINT}{' '}
                <Link to={buildVenueSuppliersPath(venueId)}>
                  Open Your Suppliers
                </Link>
                .
              </AutoSendBody>
              {autoSendGate.message ? (
                <AutoSendWarn $tone={autoSendGate.tone}>
                  {autoSendGate.message}
                </AutoSendWarn>
              ) : null}
              {showMapSuppliersCta ? (
                <MapSuppliersCta>
                  <AutoSendWarn $tone='warning'>
                    {XERO_AUTO_SEND_MAP_SUPPLIERS_HINT}
                  </AutoSendWarn>
                  <Link to={buildVenueSuppliersPath(venueId)}>
                    <Button type='primary'>
                      {XERO_AUTO_SEND_MAP_SUPPLIERS_CTA}
                    </Button>
                  </Link>
                </MapSuppliersCta>
              ) : null}
            </div>
            <Switch
              checked={automaticSendToXero}
              disabled={autoSendLoading || autoSendGate.disabled}
              loading={autoSendLoading}
              onChange={async checked => {
                if (autoSendGate.disabled) return
                setAutoSendLoading(true)
                await dispatch(
                  editVenue(buildXeroAutoSendVenueUpdate(checked), venueId),
                )
                setAutoSendLoading(false)
              }}
            />
          </AutoSendRow>
        </Card>

        {venue?.xeroConnection?.accessToken && (
          <Card>
            <SectionHeader>
              <h3>Tracking Categories</h3>
              <Button
                onClick={async () => {
                  if (!selectedOrgId) return
                  await dispatch(
                    refreshXeroTrackingCategories(venueId, selectedOrgId),
                  )
                  await dispatch(
                    fetchXeroTrackingCategories(venueId, selectedOrgId),
                  )
                }}
                loading={trackingLoading}
              >
                Refresh from Xero
              </Button>
            </SectionHeader>

            {trackingNotApplied && (
              <LightWarning>
                Tracking not applied. You can still send to Xero, but no
                tracking will be included on line items.
              </LightWarning>
            )}

            {xeroOrgs.length > 1 && (
              <FieldRow>
                <FieldLabel>Organization</FieldLabel>
                <Select
                  style={{ width: '100%' }}
                  value={selectedOrgId}
                  onChange={value => setSelectedOrgId(value)}
                >
                  {xeroOrgs.map(org => (
                    <Option key={org.id} value={org.id}>
                      {org.name}
                    </Option>
                  ))}
                </Select>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>Tracking Category</FieldLabel>
              <Select
                style={{ width: '100%' }}
                placeholder={trackingLoading ? 'Loading...' : 'Select category'}
                value={selectedTrackingCategoryId || undefined}
                onChange={value => {
                  setSelectedTrackingCategoryId(value || '')
                  setSelectedTrackingOptionId('')
                }}
                allowClear
                disabled={trackingLoading}
                notFoundContent={trackingLoading ? <Spin size='small' /> : null}
              >
                {trackingCategories.map(cat => (
                  <Option
                    key={cat.TrackingCategoryID}
                    value={cat.TrackingCategoryID}
                  >
                    {cat.Name}
                  </Option>
                ))}
              </Select>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Tracking Option</FieldLabel>
              <Select
                style={{ width: '100%' }}
                placeholder={
                  trackingLoading
                    ? 'Loading...'
                    : selectedTrackingCategoryId
                    ? 'Select option'
                    : 'Select a category first'
                }
                value={selectedTrackingOptionId || undefined}
                onChange={value => setSelectedTrackingOptionId(value || '')}
                allowClear
                disabled={trackingLoading || !selectedTrackingCategoryId}
                notFoundContent={trackingLoading ? <Spin size='small' /> : null}
              >
                {trackingOptions.map(opt => (
                  <Option
                    key={opt.TrackingOptionID}
                    value={opt.TrackingOptionID}
                  >
                    {opt.Name}
                  </Option>
                ))}
              </Select>
            </FieldRow>

            <ButtonGroup>
              <Button
                type='primary'
                loading={trackingSaving}
                onClick={async () => {
                  if (!selectedOrgId) return
                  const result = await dispatch(
                    saveXeroTrackingMapping(
                      venueId,
                      selectedOrgId,
                      selectedTrackingCategoryId,
                      selectedTrackingOptionId,
                    ),
                  )
                  if (result?.success) {
                    dispatch(getVenue(venueId))
                  }
                }}
              >
                Save tracking settings
              </Button>
            </ButtonGroup>
          </Card>
        )}
      </Section>

      {/* Modal for collecting finance email when connecting */}
      <Modal
        title='Connect to Xero'
        visible={isXeroModalVisible}
        onCancel={() => setIsXeroModalVisible(false)}
        footer={[
          <Button key='back' onClick={() => setIsXeroModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key='submit'
            type='primary'
            onClick={handleXeroModalSubmit}
            loading={isLoading}
          >
            Save & Connect Xero
          </Button>,
        ]}
      >
        <ModalContent>
          <label>Who should receive Xero updates and reminders?</label>
          <Input
            placeholder='Finance Contact Email'
            value={newXeroEmail}
            onChange={e => setNewXeroEmail(e.target.value)}
            style={{ marginTop: '8px' }}
          />
          <ModalHelperText>
            {` (Optional – You can enter a shared inbox like accounts@yourbusiness.com)`}
          </ModalHelperText>
          <ModalExplanation>
            {`Xero connections need to be re-authorised every 90 days for security. We'll email this address when it's time to reconnect so nothing gets missed.`}
          </ModalExplanation>
        </ModalContent>
      </Modal>

      {/* Modal for disconnecting with warning about linked venues */}
      <Modal
        title='Disconnect Xero'
        visible={isDisconnectModalVisible}
        onCancel={() => setIsDisconnectModalVisible(false)}
        footer={[
          <Button key='back' onClick={() => setIsDisconnectModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key='submit'
            type='primary'
            danger
            onClick={handleConfirmDisconnect}
            loading={isLoading}
          >
            Disconnect All Venues
          </Button>,
        ]}
      >
        <ModalContent>
          {linkedVenuesInfo && linkedVenuesInfo.totalCount > 1 ? (
            <>
              <WarningText>
                ⚠️ <strong>Warning:</strong> This Xero organization (
                {linkedVenuesInfo.organizationName}) is currently linked to{' '}
                {linkedVenuesInfo.totalCount} venue(s).
              </WarningText>
              <p style={{ marginTop: '12px', marginBottom: '8px' }}>
                Disconnecting will affect the following venues:
              </p>
              <VenueList>
                <li>
                  <strong>{venue?.title}</strong> (current venue)
                </li>
                {linkedVenuesInfo.linkedVenues?.map(v => (
                  <li key={v._id}>{v.title}</li>
                ))}
              </VenueList>
              <p style={{ marginTop: '12px', color: '#666' }}>
                All {linkedVenuesInfo.totalCount} venues will lose access to
                Xero integration.
              </p>
            </>
          ) : (
            <p>Are you sure you want to disconnect this venue from Xero?</p>
          )}
        </ModalContent>
      </Modal>

      {/* Link Other Venues Modal */}
      <LinkVenuesModal
        visible={isLinkVenuesModalVisible}
        onClose={() => setIsLinkVenuesModalVisible(false)}
        venueId={venueId}
      />
      <SendUnsentXeroModal
        visible={isSendUnsentVisible}
        onClose={() => setIsSendUnsentVisible(false)}
        venueId={venueId}
        onComplete={refreshXeroHealth}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: ${({ $embedded }) => ($embedded ? '0' : '20px')};
  background-color: #f9f9f9;
  min-height: ${({ $embedded }) => ($embedded ? '0' : '100vh')};
`

const Section = styled.div`
  margin-bottom: 20px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`

const Card = styled.div`
  background-color: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
`

const XeroAccountInfo = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  background-color: #f0f7ff;
  border-radius: 6px;
  font-size: 14px;
  color: #333;

  strong {
    color: #1890ff;
  }
`

// const EmailContainer = styled.div`
//   margin: 15px 0;
//   padding: 10px;
//   background-color: #f8f8f8;
//   border-radius: 5px;

//   label {
//     font-weight: 600;
//     margin-bottom: 5px;
//     display: block;
//   }
// `

// const EmailDisplay = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 10px;
// `

// const EditIcon = styled(EditOutlined)`
//   color: #1890ff;
//   cursor: pointer;
// `

// const EmailEditContainer = styled.div`
//   margin-top: 5px;
// `

// const ButtonGroup = styled.div`
//   display: flex;
//   gap: 10px;
//   margin-top: 10px;
//   justify-content: flex-end;
// `

// const HelperText = styled.p`
//   font-size: 12px;
//   color: #666;
//   margin-top: 5px;
// `

const ModalContent = styled.div`
  margin-bottom: 15px;

  label {
    font-weight: 600;
    display: block;
    margin-bottom: 5px;
  }
`

const ModalHelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 8px;
  margin-bottom: 0;
`

const ModalExplanation = styled.p`
  font-size: 14px;
  color: #666;
  margin-top: 15px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
`

const FieldRow = styled.div`
  margin-top: 12px;
`

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #444;
  margin-bottom: 6px;
`

const HealthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
`

const HealthItem = styled.div`
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 6px;
  font-size: 14px;
`

const HealthLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
`

const LightWarning = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  color: #8c6d1f;
  font-size: 13px;
`

const WarningText = styled.p`
  color: #fa8c16;
  font-size: 14px;
  margin-bottom: 8px;
`

const VenueList = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  margin-top: 8px;

  li {
    margin: 4px 0;
  }
`

const AutoSendRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`

const AutoSendTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
`

const AutoSendBody = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #595959;
`

const AutoSendWarn = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: ${props => (props.$tone === 'info' ? '#096dd9' : '#d48806')};
`

const MapSuppliersCta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-top: 4px;

  ${AutoSendWarn} {
    margin: 0;
  }
`

export default Integrations
